import { ProfileData } from '../types';

export const DEFAULT_PROFILE: ProfileData = {
  id: 'karl-wakley',
  name: 'Karl Wakley',
  title: 'Head of Product',
  email: 'kwakley@rethinkdemand.com',
  phone: '7472308800',
  location: 'GMT (London)',
  address: '10 Downing Street, London',
  linkedin: 'linkedin.com/in/karlwakley',
  archetype: 'High Influence Archetype',
  bestContact: 'Tue-Thu, 9am-11am',
  bio: 'Experienced Commercial Director with a demonstrated history of leadership. Evaluates decisions by considering multiple perspectives and data-driven insights.',
  avatarUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Karl&backgroundColor=FA6284',
  company: {
    name: 'Rethink Demand',
    industry: 'Marketing',
    size: '1-10 Employees',
    website: 'rethinkdemand.com'
  },
  engagementHistory: [
    { type: 'Downloaded Whitepaper', asset: 'Top tips for building a webpage', date: 'Jan 27, 2026', intent: 'High' },
    { type: 'Initial Lead Generation', asset: 'Database Capture', date: 'Dec 12, 2025', intent: 'Low' }
  ],
  ocean: {
    openness: 85,
    conscientiousness: 65,
    extraversion: 92,
    agreeableness: 78,
    neuroticism: 18
  },
  communication: {
    description: 'Engaging, Direct, and Human. Communicates in an engaging manner, often addressing his audience directly and using a friendly tone.',
    keywords: ['Direct', 'Human', 'Engaging', 'Moderate Density']
  },
  decisionMaking: {
    description: 'Karl evaluates decisions by considering multiple perspectives and data-driven insights. He values collaboration and seeks input from others.',
    pace: 'Cautious / Slow',
    risk: 'Medium Tolerance'
  },
  insights: {
    closingSecret: 'Present data-driven insights that align with his strategic goals. He appreciates clarity and directness in proposals.',
    clearAnswerProb: 'Likely to avoid saying no directly; hesitance indicates need for further discussion.',
    motivators: ['Innovation & Creativity', 'Meaningful Connections', 'Impactful Results'],
    phrasesToUse: ["Let's collaborate on...", "Excited to Share", "Multiple perspectives"],
    dislikes: ['Aggressive sales tactics', 'Overly complex jargon', 'Rushing evaluation']
  },
  meetings: {
    dos: ['Present data clearly', 'Friendly / Casual tone', 'Seek collaboration'],
    donts: ['Avoid jargon gaps', 'Avoid being aggressive', 'Don\'t rush evaluation']
  },
  coldCalling: {
    patternInterrupt: 'Use a friendly and casual tone, make it feel like a conversation with an old friend.',
    pace: 'Maintain a moderate pace, ensuring clarity without sounding rushed.',
    tone: 'Speak with high energy and enthusiasm to capture his attention.',
    tactics: 'Emphasize the strategic benefits of your proposal.',
    mistakes: 'Avoid being overly aggressive in your approach.',
    ask: 'Frame your ask in a positive light, showing how it benefits him.',
    subconsciousDriver: 'Desire for meaningful connections and impactful results.'
  },
  emailGuidelines: {
    subjectExample: 'Catchy and personalized. Example: "Mark, let\'s innovate together!"',
    salutation: 'Use a friendly greeting like "Hi Mark" or "Hey Mark"',
    greeting: 'Start with an engaging line like "Hope you\'re having a great week!"',
    bulletPoints: 'Use bullet points to clarify key messages.',
    closingLine: 'End with an enthusiastic call to action.',
    signOff: 'Friendly sign-off like "Best regards" or "Excited to connect!"',
    tone: 'Maintain a friendly and approachable tone throughout.',
    length: 'Keep emails concise, ideally around 100-150 words.'
  },
  socialActivity: {
    posts: [
      { id: 0, topic: 'Lead Gen Insights', content: 'Stop measuring MQL volume and start measuring conversation velocity. Depth is the new scale.', date: '2 days ago' },
      { id: 1, topic: 'Innovation', content: 'Innovation isn\'t about the newest tech, it\'s about the smartest application of the data you already have.', date: '1 week ago' }
    ]
  }
};

export const OCEAN_TRAITS = [
  { 
    label: 'Openness', 
    key: 'openness' as const, 
    color: 'bg-brand-pink', 
    desc: 'Intellectual curiosity, creative imagination, and preference for novelty.' 
  },
  { 
    label: 'Conscientiousness', 
    key: 'conscientiousness' as const, 
    color: 'bg-brand-aqua', 
    desc: 'Self-discipline, organization, and a preference for planned rather than spontaneous behavior.' 
  },
  { 
    label: 'Extraversion', 
    key: 'extraversion' as const, 
    color: 'bg-brand-mustard', 
    desc: 'Energy, sociability, assertiveness, and tendencies to seek stimulation in the company of others.' 
  },
  { 
    label: 'Agreeableness', 
    key: 'agreeableness' as const, 
    color: 'bg-brand-pink', 
    desc: 'Tendency to be compassionate and cooperative rather than suspicious and antagonistic.' 
  },
  { 
    label: 'Neuroticism', 
    key: 'neuroticism' as const, 
    color: 'bg-brand-aqua', 
    desc: 'Sensitivity to stress and tendency to experience unpleasant emotions like anxiety or anger.' 
  },
];

export const TABS = [
  { id: 'general' as const, label: '01 Profile & History' },
  { id: 'personality' as const, label: '02 Personality Intel' },
  { id: 'outreach' as const, label: '03 Outreach Co-Pilot' },
  { id: 'social' as const, label: '04 Social Activity' }
];

export const PRESET_PROFILES = {
  'Enterprise Sales Leader': {
    name: 'David Chen',
    title: 'VP of Enterprise Sales',
    industry: 'Cloud Infrastructure',
    companySize: '1000+ Employees',
    segment: 'Global Enterprise Accounts',
    objective: 'Q4 Sales Strategy Realignment'
  },
  'Startup Founder': {
    name: 'Elena Rossi',
    title: 'Co-Founder & CEO',
    industry: 'AI Ethics',
    companySize: '11-50 Employees',
    segment: 'Early Stage Innovators',
    objective: 'Series A Fundraising Narrative'
  },
  'Mid-Market CTO': {
    name: 'Marcus Thorne',
    title: 'Chief Technology Officer',
    industry: 'E-commerce Logistics',
    companySize: '201-500 Employees',
    segment: 'Scaling Retail Brands',
    objective: 'Technical Debt Modernization'
  }
};

export const COMPANY_SIZE_OPTIONS = [
  '1-10 Employees',
  '11-50 Employees',
  '51-200 Employees',
  '201-500 Employees',
  '501-1000 Employees',
  '1000+ Employees'
];

export const CHANNEL_OPTIONS = [
  'Cold Email',
  'LinkedIn DM',
  'Follow-up'
];

export const TONE_OPTIONS = [
  'Friendly / Casual',
  'Assertive / Warm',
  'Direct / Data-led'
];

export const COMMENT_TYPES: Array<{ id: 'supportive' | 'insightful' | 'question', label: string }> = [
  { id: 'supportive', label: 'Supportive' },
  { id: 'insightful', label: 'Insightful' },
  { id: 'question', label: 'Question' }
];
